import { useModalContext } from '#pplx/frontend/aether/modals/context/useModalContext.js';
import { useToastContext } from '#pplx/frontend/aether/toasts/context/useToastContext.js';
import { getCometDownloadURL } from '#pplx/frontend/apps/ask/queries/comet-queries/getCometDownloadURL.js';
import { addToCometUserCohort } from '#pplx/frontend/apps/ask/queries/upsell-queries/addToUserCohort.js';
import { setCookieHasDownloadedComet } from '#pplx/frontend/cookies/metadataCookieManager.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useSingularAttribution } from '#pplx/frontend/growth/analytics-singular/SingularAttributionProviderShared.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { redirectWindow } from '#pplx/frontend/platform-components/redirectWindow.js';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
// Single-file event: defined here at its only call site rather than in a shared package.
const clickDownloadCometEvent = EVENT_PAYLOAD;
const LazyCometDownloadInstructionsModal = Dynamic(async () => {
    const { CometDownloadInstructionsModal } = await import('#pplx/frontend/apps/ask/comet-download/components/CometDownloadInstructionsModal.js');
    return { default: CometDownloadInstructionsModal };
});
export const useClickDownloadComet = ({ isUnsupportedDevice, isWindows, }) => {
    const { track } = useInstrumentation();
    const { sendCometDownloadedEventSingular, sendCometDownloadButtonClickedEventSingular } = useSingularAttribution();
    const { openToast } = useToastContext();
    const { openModal } = useModalContext();
    const { $t } = useIntl();
    const handleDownloadSuccessMessage = useCallback(async (preventDownloadInstructionsModal, requestedPlatform) => {
        openToast({
            message: $t({
                defaultMessage: 'Comet is downloading',
                description: 'Toast message title shown when the Comet download starts',
                id: '4a3b1d7c8f',
            }),
            description: $t({
                defaultMessage: "Check your browser's downloads to open the Comet installer.",
                description: 'Toast message description guiding the user to find the Comet installer in browser downloads',
                id: 'b9f63d68d6',
            }),
            iconOverride: $icon('download'),
            variant: 'success',
            timeoutSeconds: 5,
        });
        if (!preventDownloadInstructionsModal && requestedPlatform) {
            openModal(LazyCometDownloadInstructionsModal, {
                requestedPlatform,
            }, { replace: true });
        }
    }, [openToast, $t, openModal]);
    const downloadComet = useCallback(async (requestedPlatform, { upsellName, partnerCode, preventDownloadInstructionsModal, }) => {
        handleDownloadSuccessMessage(preventDownloadInstructionsModal, requestedPlatform);
        // Mark that user has downloaded Comet
        setCookieHasDownloadedComet();
        track(clickDownloadCometEvent({
            name: 'click download comet',
            data: {
                requestedPlatform,
                upsellName,
                installPartner: partnerCode,
            },
        }));
        sendCometDownloadedEventSingular();
        // twiddling the href swallows the event unless we do this
        setTimeout(() => redirectWindow(getCometDownloadURL({ platform: requestedPlatform }).href, 'Comet download'), 250);
    }, [track, sendCometDownloadedEventSingular, handleDownloadSuccessMessage]);
    const downloadVariant2 = useCallback(async ({ upsellName, partnerCode, preventDownloadInstructionsModal, }) => {
        // Safety check: don't download anything on unsupported
        if (isUnsupportedDevice) {
            return;
        }
        // Add user to Comet users cohort to prevent showing further Comet upsells to this user
        await addToCometUserCohort();
        return isWindows
            ? downloadComet('win_x64', {
                upsellName,
                partnerCode,
                preventDownloadInstructionsModal,
            })
            : downloadComet('mac_arm64', {
                upsellName,
                partnerCode,
                preventDownloadInstructionsModal,
            });
    }, [downloadComet, isWindows, isUnsupportedDevice]);
    const handleDownloadClick = useCallback(({ upsellName, partnerCode }) => {
        sendCometDownloadButtonClickedEventSingular('download-comet');
        downloadVariant2({ upsellName, partnerCode });
    }, [sendCometDownloadButtonClickedEventSingular, downloadVariant2]);
    return { handleDownload: downloadVariant2, handleDownloadClick };
};
//# sourceMappingURL=useClickDownloadComet.js.map